import { type Ref, computed } from "vue";

import {
  type Dimensions,
  DimensionsMode,
} from "@/models/borrowerPlatformTemplates";

export interface DimensionsConfig {
  dimensions?: Dimensions;
}

export function useDimensions<T extends DimensionsConfig>(
  modelValue: Ref<T>,
  updateCallback: (updates: Partial<T>) => void,
  defaultScale = 100
) {
  const dimensions = computed(() => {
    return (
      modelValue.value.dimensions || {
        mode: DimensionsMode.SCALE,
        scale: defaultScale,
      }
    );
  });

  const isFullWidth = computed(() => {
    return dimensions.value.mode === DimensionsMode.FULL_WIDTH;
  });

  const scale = computed(() => {
    if (dimensions.value.mode === DimensionsMode.SCALE) {
      return dimensions.value.scale !== null ? dimensions.value.scale : "";
    }
    return defaultScale;
  });

  const handleFullWidthToggle = (isFullWidth: boolean) => {
    const newDimensions = isFullWidth
      ? { mode: DimensionsMode.FULL_WIDTH }
      : {
          mode: DimensionsMode.SCALE,
          scale:
            dimensions.value.mode === DimensionsMode.SCALE
              ? (dimensions.value.scale ?? defaultScale)
              : defaultScale,
        };

    updateCallback({ dimensions: newDimensions } as Partial<T>);
  };

  const handleScaleChange = (value: string, min = 1, max = 100, step = 1) => {
    if (!value) {
      updateCallback({
        dimensions: {
          mode: DimensionsMode.SCALE,
          scale: null,
        },
      } as Partial<T>);
      return;
    }

    let newScale = Number(value);

    if (isNaN(newScale)) {
      return;
    }

    newScale = Math.max(min, Math.min(max, newScale));

    if (step > 0) {
      newScale = Math.round((newScale - min) / step) * step + min;
    }

    updateCallback({
      dimensions: {
        mode: DimensionsMode.SCALE,
        scale: newScale,
      },
    } as Partial<T>);
  };

  return {
    dimensions,
    isFullWidth,
    scale,
    handleFullWidthToggle,
    handleScaleChange,
  };
}
